import type { ReactNode } from 'react';
import { ChevronBand, Halftone, Paper, Rule, Label } from '../design/Paper';
import { Tracker } from '../design/Tracker';
import { ro } from '../i18n/ro';
import { VoteOpening } from './VoteOpening';

/**
 * The shared-screen chrome. Declares the size container that lets every child
 * size itself in cqw/cqh, so the same components render identically full-screen
 * and inside a preview frame.
 */
export function ProjectorFrame({
  kicker,
  meta,
  failedElections,
  announceVote = false,
  children,
}: {
  kicker: string;
  meta?: string;
  failedElections: number;
  /** Plays the vote-opening announcement over the whole screen, once. */
  announceVote?: boolean;
  children: ReactNode;
}) {
  return (
    <Paper className="projector flex flex-col" data-testid="projector">
      <header className="relative shrink-0 bg-ink px-scr-gut py-scr-2 text-paper">
        <Halftone className="text-paper" />
        <div className="relative flex items-baseline justify-between gap-scr-gut">
          <Label className="text-screen-md" data-testid="projector-kicker">
            {kicker}
          </Label>
          <div className="flex items-baseline gap-scr-gut">
            {meta && (
              <Label className="text-screen-md" data-testid="projector-meta">
                {meta}
              </Label>
            )}
            <Label className="text-screen-sm opacity-55">{ro.appName}</Label>
          </div>
        </div>
      </header>
      <ChevronBand className="shrink-0 text-ink" />

      <main className="relative flex min-h-0 flex-1 flex-col items-center justify-center gap-scr-3 overflow-hidden px-scr-gut py-scr-3">
        {children}
      </main>

      <VoteOpening trigger={announceVote} />

      <footer className="shrink-0 px-scr-gut pb-scr-2">
        <Rule className="mb-scr-2" />
        <Tracker failedElections={failedElections} size="lg" />
      </footer>
    </Paper>
  );
}
